import { ArrowLeft } from "lucide-react"
import { type ReactNode, useContext } from "react"
import { Link, useLocation } from "react-router-dom"
import { cn } from "@/shared/lib/utils"
import { LoadSwap } from "@/shared/ui/load-swap"
import { PageShell } from "@/shared/ui/page-shell"
import { Skeleton } from "@/shared/ui/skeleton"
import { botPath, InnerPageContext, useOptionalBotWorkspace } from "../model/bot-workspace-context"
import { findNavItem } from "../model/workspace-nav"

interface WorkspaceSectionProps {
	/** `null`, пока название страницы грузится: на его месте заглушка. */
	title: ReactNode
	/** Пояснение под заголовком, как описания разделов в Botmaker. */
	subtitle?: ReactNode
	/** Кнопки справа от заголовка: «Добавить воронку», «Добавить метку»… */
	actions?: ReactNode
	/** Объяснение раздела простыми словами под заголовком. */
	help?: ReactNode
	/**
	 * Надзаголовок внутренней страницы: состояние того, что на ней («Черновик · сохранено»).
	 * Без него там стоит имя бота.
	 */
	eyebrow?: ReactNode
	children: ReactNode
	className?: string
}

/** Подпись, которая меняется перекрёстно: состояние в надзаголовке, название после загрузки. */
function Swap({ id, children }: { id: string; children: ReactNode }) {
	return (
		<LoadSwap loading={false} id={id} autoHeight={false}>
			{children}
		</LoadSwap>
	)
}

/**
 * Страница раздела бота: заголовок, пояснение и действия, под ними содержимое. Карточкой
 * страница не обёрнута: карточки рисует содержимое, иначе выходит карточка в карточке.
 * Без своего пояснения берётся описание пункта меню. Внутренняя страница (одна рассылка,
 * один пользователь) рисует крупную шапку, как у материала в «Контенте»: ссылка назад к
 * списку, надзаголовок, название и действия. Вне раздела бота это обычная страница панели.
 */
export function WorkspaceSection({
	title,
	subtitle,
	actions,
	help,
	eyebrow,
	children,
	className,
}: WorkspaceSectionProps) {
	const workspace = useOptionalBotWorkspace()
	const inner = useContext(InnerPageContext)
	const location = useLocation()
	if (!workspace) {
		return (
			<PageShell title={title} subtitle={typeof subtitle === "string" ? subtitle : undefined}>
				{actions ? <div className="mb-4 flex flex-wrap gap-2">{actions}</div> : null}
				{children}
			</PageShell>
		)
	}

	if (inner) {
		const eyebrowText = eyebrow ?? inner.botName
		return (
			<section className={cn("space-y-6", className)}>
				<div>
					<Link
						to={inner.back.to}
						className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
					>
						<ArrowLeft className="size-4" />
						{inner.back.label}
					</Link>
					<header className="flex flex-wrap items-start justify-between gap-4">
						<div className="min-w-0 flex-1">
							<Swap id={typeof eyebrowText === "string" ? eyebrowText : "eyebrow"}>
								<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
									{eyebrowText}
								</p>
							</Swap>
							<LoadSwap
								loading={title === null || title === undefined}
								id={typeof title === "string" ? title : "title"}
								autoHeight={false}
							>
								{title === null || title === undefined ? (
									<div aria-hidden="true" className="mt-1 flex h-8 items-center sm:h-9">
										<Skeleton className="h-6 w-64 max-w-full rounded-lg sm:h-7" />
									</div>
								) : (
									<h1 className="mt-1 text-2xl font-bold tracking-tight break-words sm:text-3xl">
										{title}
									</h1>
								)}
							</LoadSwap>
							{subtitle ? (
								<div className="mt-2 max-w-2xl text-sm leading-snug text-muted-foreground">
									{subtitle}
								</div>
							) : null}
						</div>
						{actions ? (
							<div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
						) : null}
					</header>
				</div>
				{help}
				{children}
			</section>
		)
	}

	const prefix = `${botPath(workspace.bot.id)}/`
	const sectionPath = location.pathname.startsWith(prefix)
		? location.pathname.slice(prefix.length)
		: ""
	const description = subtitle ?? findNavItem(sectionPath)?.description

	return (
		<section className={cn("space-y-5", className)}>
			<header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
				<div className="min-w-0 flex-1">
					<h2 className="text-lg font-semibold tracking-tight">{title}</h2>
					{description ? (
						<div className="mt-1 max-w-2xl text-sm leading-snug text-muted-foreground">
							{description}
						</div>
					) : null}
				</div>
				{actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
			</header>
			{help}
			{children}
		</section>
	)
}
